import MentalMap from "./MentalMap";
import MindMapContainer from "./ListMap";
import KnowledgeBaseMenu from "./components/KnowledgeBaseMenu/KnowledgeBaseMenu";
import TREEPERSONAL from "./initialElements-personal";
import TREEBUSINESS from "./initialElements-business";
import TREECLIENTMENTOR from "./initialElements-client-mentor";
import { useState, useEffect, useCallback, useMemo } from "react";
import Sidebar from "./sidebar";
import { useAuth } from "./components/auth/AuthContext";
import CreditPlanFlow from "./components/CreditPlanFlow/CreditPlanFlow";
import ActionablePlans from "./components/CreditPlanFlow/ActionablePlans";
import AssessmentHistory from "./components/CreditPlanFlow/AssessmentHistory";
import DemoPlanFlow from "./components/CreditPlanFlow/DemoPlanFlow";
import CreditCardsInventory from "./components/CreditCardsInventory/CreditCardsInventory";
import { ParseCreditReport } from "./components/CreditReportParser";
import { Switch, Route, useHistory, useLocation } from "react-router-dom";
import ManageSubscription from "./components/ManageSubscription";
import UserProfile from "./components/auth/UserProfile";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsAndConditions from "./components/TermsAndConditions";
import CookiePolicy from "./components/CookiePolicy";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { useAnalytics } from "./hooks/useAnalytics";
import LanguageSelector from "./components/language/LanguageSelector";
import UserMenu from "./components/navbar/UserMenu";
import NavDropdown from "./components/navbar/NavDropdown";
import { useTranslation } from "./hooks/useTranslation";
import { MasqueradeBanner } from "./components/mentor/MasqueradeBanner";
import { ClientInvitationBanner } from "./components/mentor/ClientInvitationBanner";
import { MentorDashboard } from "./components/mentor/MentorDashboard";
import { ViewSwitcher } from "./components/mentor/ViewSwitcher";
import { useTier } from "./hooks/useTier";
import { useSubscription } from "./hooks/useSubscription";
import { getTier } from "./services/tierService";
import { useScope } from "./hooks/useScope";
import { isPathAllowedForClient, getPermissions } from "./config/permissions";
import LandingPage from "./pages/LandingPage";

// Helper to find a node by page slug in a tree
function findNodeByPageSlug(tree, pageSlug) {
  for (const [nodeId, nodeData] of Object.entries(tree)) {
    if (nodeData.page) {
      // Normalize the page path for comparison (remove .html extension if present)
      const normalizedNodePage = nodeData.page.replace(/\.html$/, "");
      const normalizedSlug = pageSlug.replace(/\.html$/, "");
      if (normalizedNodePage === normalizedSlug) {
        return { id: nodeId, ...nodeData };
      }
    }
  }
  return null;
}

// Helper to get page slug from node (used for URL updates)
function getPageSlug(node) {
  if (!node?.page) return null;
  // Remove .html extension and the folder prefix to create a clean slug
  return node.page.replace(/\.html$/, "");
}

function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const history = useHistory();
  const location = useLocation();
  const { currentUser, signOut } = useAuth();
  const analytics = useAnalytics();
  const t = useTranslation();
  const { tierId } = useTier();
  const { priceId } = useSubscription();
  const { isClientView, isManaged, getRole } = useScope();

  // Get current role and permissions
  const currentRole = getRole();
  const permissions = useMemo(() => getPermissions(currentRole), [currentRole]);

  // Check if user has Elite Annual subscription
  // Use tier configuration from tiers.json instead of hardcoding
  const eliteTier = useMemo(() => getTier("elite"), []);
  const eliteAnnualPriceId = useMemo(
    () => eliteTier?.stripe?.priceId?.yearly || null,
    [eliteTier]
  );
  const isEliteAnnual = tierId === "elite" && priceId === eliteAnnualPriceId;

  // Get current tree based on path
  const currentTree = useMemo(() => {
    return location.pathname.startsWith("/business-credit")
      ? TREEBUSINESS
      : TREEPERSONAL;
  }, [location.pathname]);

  // Handle deep linking via ?page= query parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const pageSlug = searchParams.get("page");

    if (pageSlug) {
      // Try to find the node in the current tree first
      let node = findNodeByPageSlug(currentTree, pageSlug);

      // If not found and we have a tree prefix, try the other tree
      if (!node) {
        const otherTree =
          currentTree === TREEPERSONAL ? TREEBUSINESS : TREEPERSONAL;
        node = findNodeByPageSlug(otherTree, pageSlug);
      }

      if (node) {
        setSelectedNode(node);
        setSidebarOpen(true);
      }
    }
  }, [location.search, currentTree]);

  // Handle window resize to detect mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const handleNodeClick = useCallback(
    (node) => {
      setSelectedNode(node);
      setSidebarOpen(true);

      // Update URL with page slug for deep linking
      const pageSlug = getPageSlug(node);
      if (pageSlug) {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set("page", pageSlug);
        history.replace({
          pathname: location.pathname,
          search: searchParams.toString(),
        });
      }
    },
    [history, location.pathname, location.search]
  );

  const handleLogout = async () => {
    try {
      await signOut();
      setMobileMenuOpen(false);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleNavClick = (path, analyticsAction) => {
    if (analyticsAction) {
      analytics.trackCTAClick(analyticsAction);
    }
    history.push(path);
    setMobileMenuOpen(false);
  };

  // Navigation menu structure with categories
  const navMenu = useMemo(() => {
    // Check if mentor is masquerading as client (viewing client's data)
    const isMentorMasquerade = isManaged && !isClientView;

    // Mentor masquerade mode: Show limited navigation for viewing client data
    if (isMentorMasquerade) {
      const menu = [
        {
          id: "tooling",
          label: t("navigation.tooling", "Tooling"),
          icon: "🛠️",
          items: [
            {
              label: t(
                "navigation.smartCreditPlanning",
                "Smart Credit Planning"
              ),
              path: "/actionable",
              icon: "📈",
              analytics: null,
            },
            {
              label: t("navigation.storedPlans", "Stored Plans"),
              path: "/history",
              icon: "📊",
              analytics: {
                section: "navigation",
                action: "navigate_to_stored_plans",
                source: "dropdown",
              },
            },
            {
              label: t(
                "navigation.creditCardsInventory",
                "Credit Cards Inventory"
              ),
              path: "/credit-cards-inventory",
              icon: "💳",
              analytics: {
                section: "navigation",
                action: "navigate_to_credit_cards_inventory",
                source: "dropdown",
              },
            },
            {
              label: t("navigation.parseCreditReport", "Parse Credit Report"),
              path: "/parse-credit-report",
              icon: "📄",
              analytics: {
                section: "navigation",
                action: "navigate_to_parse_credit_report",
                source: "dropdown",
              },
            },
          ],
        },
        {
          id: "mentor",
          label: t("navigation.clients", "Clients"),
          icon: "👥",
          items: [
            {
              label: t("navigation.manageClients", "Manage Clients"),
              path: "/mentor/clients",
              icon: "👥",
              analytics: {
                section: "navigation",
                action: "navigate_to_mentor_clients",
                source: "dropdown",
              },
            },
          ],
        },
      ];
      return menu;
    }

    // Client view: Only show allowed navigation items
    if (isClientView) {
      return [
        {
          id: "client-sops",
          label: t("navigation.clientsSOPs", "Clients SOPs"),
          icon: "📚",
          items: [
            {
              label: t("navigation.clientsSOPs", "Clients SOPs"),
              path: "/client-mentor-sops",
              icon: "📚",
              analytics: {
                section: "navigation",
                action: "navigate_to_client_sops",
                source: "dropdown",
              },
            },
          ],
        },
        {
          id: "tooling",
          label: t("navigation.tooling", "Tooling"),
          icon: "🛠️",
          items: [
            {
              label: t("navigation.storedPlans", "Stored Plans"),
              path: "/history",
              icon: "📊",
              analytics: {
                section: "navigation",
                action: "navigate_to_stored_plans",
                source: "dropdown",
              },
            },
            {
              label: t(
                "navigation.creditCardsInventory",
                "Credit Cards Inventory"
              ),
              path: "/credit-cards-inventory",
              icon: "💳",
              analytics: {
                section: "navigation",
                action: "navigate_to_credit_cards_inventory",
                source: "dropdown",
              },
            },
            {
              label: t("navigation.parseCreditReport", "Parse Credit Report"),
              path: "/parse-credit-report",
              icon: "📄",
              analytics: {
                section: "navigation",
                action: "navigate_to_parse_credit_report",
                source: "dropdown",
              },
            },
          ],
        },
      ];
    }

    // Regular/Mentor view: Full navigation
    const menu = [
      {
        id: "credit-knowledge",
        label: t("navigation.creditKnowledge", "Credit Knowledge"),
        icon: "📚",
        items: [
          {
            label: t("navigation.personalCredit", "Personal Credit"),
            path: "/personal-credit",
            icon: "👤",
            analytics: {
              section: "navigation",
              action: "navigate_to_personal_credit",
              source: "dropdown",
            },
          },
          {
            label: t("navigation.businessCredit", "Business Credit"),
            path: "/business-credit",
            icon: "💼",
            analytics: null,
          },
        ],
      },
      {
        id: "tooling",
        label: t("navigation.tooling", "Tooling"),
        icon: "🛠️",
        items: [
          {
            label: t("navigation.smartCreditPlanning", "Smart Credit Planning"),
            path: "/actionable",
            icon: "📈",
            analytics: null,
          },
          {
            label: t("navigation.storedPlans", "Stored Plans"),
            path: "/history",
            icon: "📊",
            analytics: {
              section: "navigation",
              action: "navigate_to_stored_plans",
              source: "dropdown",
            },
          },
          {
            label: t(
              "navigation.creditCardsInventory",
              "Credit Cards Inventory"
            ),
            path: "/credit-cards-inventory",
            icon: "💳",
            analytics: {
              section: "navigation",
              action: "navigate_to_credit_cards_inventory",
              source: "dropdown",
            },
          },
          // Parse Credit Report - only for Pro and Elite tiers
          ...(tierId === "pro" || tierId === "elite"
            ? [
                {
                  label: t(
                    "navigation.parseCreditReport",
                    "Parse Credit Report"
                  ),
                  path: "/parse-credit-report",
                  icon: "📄",
                  analytics: {
                    section: "navigation",
                    action: "navigate_to_parse_credit_report",
                    source: "dropdown",
                  },
                },
              ]
            : []),
        ],
      },
      {
        id: "demos",
        label: t("navigation.demos", "Demos"),
        icon: "🎭",
        items: [
          {
            label: t("navigation.viewDemoPlan", "View Demo Plan"),
            path: "/demo",
            icon: "🎭",
            analytics: {
              section: "navigation",
              action: "navigate_to_demo_plan",
              source: "dropdown",
            },
          },
        ],
      },
    ];

    // Add Clients menu for Elite Annual users
    if (isEliteAnnual) {
      menu.push({
        id: "mentor",
        label: t("navigation.clients", "Clients"),
        icon: "👥",
        items: [
          {
            label: t("navigation.manageClients", "Manage Clients"),
            path: "/mentor/clients",
            icon: "👥",
            analytics: {
              section: "navigation",
              action: "navigate_to_mentor_clients",
              source: "dropdown",
            },
          },
        ],
      });
    }

    return menu;
  }, [t, isEliteAnnual, isClientView, isManaged, tierId]);

  // Flattened list for mobile menu (all items)
  const getAllNavItems = () => {
    const items = [];
    navMenu.forEach((category) => {
      category.items.forEach((item) => {
        items.push({
          ...item,
          categoryLabel: category.label,
          categoryIcon: category.icon,
        });
      });
    });
    return items;
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      {/* Masquerade Banner - Shows when mentor is viewing client data */}
      <MasqueradeBanner />

      {/* Client Invitation Banner - Shows pending mentor invitations */}
      <ClientInvitationBanner />

      {/* Top Navigation Bar */}
      {location.pathname !== "/" && (
        <nav className="top-nav">
          <div className="nav-left">
            {/* Mobile Hamburger Button */}
            <button
              className="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
            >
              <span className={`hamburger ${mobileMenuOpen ? "open" : ""}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
            <div
              className="nav-logo"
              onClick={() =>
                handleNavClick("/", {
                  section: "navigation",
                  action: "navigate_home_logo",
                  source: "logo",
                })
              }
              style={{ cursor: "pointer" }}
            >
              <img
                src={process.env.PUBLIC_URL + "/images/stoiccredit-logo.png"}
                alt="StoicCredit"
                style={{
                  width: "80px",
                  height: "80px",
                  marginRight: "10px",
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                }}
              />
              <span>StoicCredit</span>
            </div>
            {/* Desktop Navigation */}
            <div className="nav-buttons-desktop">
              {navMenu.map((category) => (
                <NavDropdown
                  key={category.id}
                  label={category.label}
                  icon={category.icon}
                  items={category.items}
                />
              ))}
            </div>
          </div>
          {/* User Menu */}
          <div className="nav-right">
            <ViewSwitcher />
            <div className="nav-grouping">
              <LanguageSelector />
              <UserMenu />
            </div>
          </div>
        </nav>
      )}

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Drawer */}
      <div className={`mobile-menu-drawer ${mobileMenuOpen ? "open" : ""}`}>
        <div className="mobile-menu-header">
          <h2>{t("navigation.menu", "Menu")}</h2>
          <button
            className="mobile-menu-close"
            onClick={() => setMobileMenuOpen(false)}
            aria-label={t("navigation.closeMenu", "Close menu")}
          >
            ×
          </button>
        </div>
        <div className="mobile-menu-content">
          {/* View Switcher for Mobile */}
          <div className="mobile-view-switcher">
            <ViewSwitcher />
          </div>
          {navMenu.map((category) => (
            <div key={category.id} className="mobile-menu-category">
              <div className="mobile-menu-category-header">
                <span className="mobile-menu-category-icon">
                  {category.icon}
                </span>
                <span className="mobile-menu-category-label">
                  {category.label}
                </span>
              </div>
              <div className="mobile-menu-category-items">
                {category.items.map((item) => {
                  const isActive =
                    item.path === "/"
                      ? location.pathname === "/" ||
                        location.pathname === "/personal-credit"
                      : location.pathname === item.path;

                  return (
                    <button
                      key={item.path}
                      className={`mobile-nav-button ${
                        isActive ? "active" : ""
                      }`}
                      onClick={() => handleNavClick(item.path, item.analytics)}
                    >
                      {item.icon && (
                        <span className="mobile-nav-icon">{item.icon}</span>
                      )}
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Main Content */}
      <main
        style={{
          flex: "1 0 auto",
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Switch>
          <Route exact path="/" component={LandingPage} />
          <Route
            path="/personal-credit"
            render={() => (
              <KnowledgeBaseMenu
                tree={TREEPERSONAL}
                onNodeClick={handleNodeClick}
                selectedNodeId={selectedNode?.id}
                treeType="personal"
              />
            )}
          />
          <Route
            path="/business-credit"
            render={() => (
              <KnowledgeBaseMenu
                tree={TREEBUSINESS}
                onNodeClick={handleNodeClick}
                selectedNodeId={selectedNode?.id}
                treeType="business"
              />
            )}
          />
          <Route
            path="/client-mentor-sops"
            render={() => (
              <KnowledgeBaseMenu
                tree={TREECLIENTMENTOR}
                onNodeClick={handleNodeClick}
                selectedNodeId={selectedNode?.id}
                treeType="client-mentor"
              />
            )}
          />
          {/* <Route path="/mental" component={MentalMap} /> */}
          <Route path="/creditplan/:planId?" component={CreditPlanFlow} />
          {/* Protect actionable (Smart Credit Planning) from client view */}
          <Route
            path="/actionable"
            render={(props) =>
              isClientView ? (
                <AssessmentHistory {...props} />
              ) : (
                <ActionablePlans {...props} />
              )
            }
          />
          <Route path="/history" component={AssessmentHistory} />
          <Route
            path="/credit-cards-inventory"
            component={CreditCardsInventory}
          />
          <Route path="/parse-credit-report" component={ParseCreditReport} />
          <Route path="/demo" component={DemoPlanFlow} />
          <Route path="/mentor/clients" component={MentorDashboard} />
          <Route path="/profile" component={UserProfile} />
          {/* Protect manage-subscription from client view */}
          <Route
            path="/manage-subscription"
            render={(props) =>
              isClientView ? (
                <AssessmentHistory {...props} />
              ) : (
                <ManageSubscription {...props} />
              )
            }
          />
          <Route path="/privacy-policy" component={PrivacyPolicy} />
          <Route path="/terms-and-conditions" component={TermsAndConditions} />
          <Route path="/cookie-policy" component={CookiePolicy} />
          <Route
            path="*"
            render={() => (
              <KnowledgeBaseMenu
                tree={TREEPERSONAL}
                onNodeClick={handleNodeClick}
                selectedNodeId={selectedNode?.id}
                treeType="personal"
              />
            )}
          />
        </Switch>
      </main>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => {
            setSidebarOpen(false);
            // Clear page param from URL
            const searchParams = new URLSearchParams(location.search);
            if (searchParams.has("page")) {
              searchParams.delete("page");
              history.replace({
                pathname: location.pathname,
                search: searchParams.toString(),
              });
            }
          }}
        />
      )}

      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => {
          setSidebarOpen(false);
          // Clear page param from URL
          const searchParams = new URLSearchParams(location.search);
          if (searchParams.has("page")) {
            searchParams.delete("page");
            history.replace({
              pathname: location.pathname,
              search: searchParams.toString(),
            });
          }
        }}
        selectedNode={selectedNode}
      />

      {/* Cookie Consent Banner */}
      <CookieConsentBanner />

      {/* Footer */}
      {location.pathname !== "/" && (
        <footer className="app-footer">
          <div className="app-footer-content">
            <span>
              © {new Date().getFullYear()} StoicCredit. All rights reserved.
            </span>
            <a
              href="/privacy-policy"
              onClick={(e) => {
                e.preventDefault();
                analytics.trackCTAClick({
                  section: "footer",
                  action: "view_privacy_policy",
                  source: "link",
                });
                history.push("/privacy-policy");
              }}
            >
              Privacy Policy
            </a>
            <span className="footer-separator">|</span>
            <a
              href="/terms-and-conditions"
              onClick={(e) => {
                e.preventDefault();
                analytics.trackCTAClick({
                  section: "footer",
                  action: "view_terms_and_conditions",
                  source: "link",
                });
                history.push("/terms-and-conditions");
              }}
            >
              Terms and Conditions
            </a>
            <span className="footer-separator">|</span>
            <a
              href="/cookie-policy"
              onClick={(e) => {
                e.preventDefault();
                analytics.trackCTAClick({
                  section: "footer",
                  action: "view_cookie_policy",
                  source: "link",
                });
                history.push("/cookie-policy");
              }}
            >
              Cookie Policy
            </a>
            <span className="footer-separator">|</span>
            <a
              href="mailto:support@stoiccredit.com"
              onClick={() => {
                analytics.trackCTAClick({
                  section: "footer",
                  action: "contact_support",
                  source: "email_link",
                });
              }}
            >
              Support
            </a>
          </div>
        </footer>
      )}
    </div>
  );
}

export default Layout;
